import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { useAcceleratingHold } from '../hooks/useAcceleratingHold';

const triggerHaptic = (duration = 10) => {
  if (typeof window !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(duration);
    } catch {
      // Ignored
    }
  }
};

export interface StepperFieldProps {
  idPrefix: string;
  unit: string;
  value: number | string;
  displayValue?: string;
  onIncrement: () => void;
  onDecrement: () => void;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  colorScheme?: 'neutral' | 'emerald' | 'blue' | 'cyan' | 'amber' | 'violet';
}

/**
 * Modern Side-by-Side Stepper:
 * - Left: Number input + Unit text inside a clean 56px container
 * - Right: Vertically stacked [+] (top half) and [-] (bottom half) buttons
 *   Together matching the EXACT same height as the input field!
 */
export default function StepperField({
  idPrefix,
  unit,
  value,
  displayValue,
  onIncrement,
  onDecrement,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  disabled = false,
  colorScheme = 'neutral',
}: StepperFieldProps) {
  const colorStyles = {
    neutral: {
      container: 'bg-white/80 dark:bg-[#1C1C28]/80 backdrop-blur-md text-slate-900 dark:text-slate-50 focus-within:ring-2 focus-within:ring-slate-400/30 dark:focus-within:ring-slate-500/30 shadow-2xs',
      btnTop: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      btnBottom: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      unitText: 'text-slate-600 dark:text-slate-400 font-semibold',
      divider: 'bg-black/5 dark:bg-white/10',
      borderLeft: 'border-black/5 dark:border-white/10',
    },
    emerald: {
      container: 'bg-white/80 dark:bg-[#1A2621]/80 backdrop-blur-md text-emerald-950 dark:text-emerald-50 focus-within:ring-2 focus-within:ring-emerald-500/30 shadow-2xs',
      btnTop: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-emerald-950 dark:text-emerald-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      btnBottom: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-emerald-950 dark:text-emerald-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      unitText: 'text-emerald-800/80 dark:text-emerald-300/80 font-semibold',
      divider: 'bg-black/5 dark:bg-white/10',
      borderLeft: 'border-black/5 dark:border-white/10',
    },
    blue: {
      container: 'bg-white/80 dark:bg-[#142333]/80 backdrop-blur-md text-blue-950 dark:text-blue-50 focus-within:ring-2 focus-within:ring-blue-500/30 shadow-2xs',
      btnTop: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-blue-950 dark:text-blue-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      btnBottom: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-blue-950 dark:text-blue-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      unitText: 'text-blue-800/80 dark:text-blue-300/80 font-semibold',
      divider: 'bg-black/5 dark:bg-white/10',
      borderLeft: 'border-black/5 dark:border-white/10',
    },
    cyan: {
      container: 'bg-white/80 dark:bg-[#14252E]/80 backdrop-blur-md text-cyan-950 dark:text-cyan-50 focus-within:ring-2 focus-within:ring-cyan-500/30 shadow-2xs',
      btnTop: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-cyan-950 dark:text-cyan-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      btnBottom: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-cyan-950 dark:text-cyan-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      unitText: 'text-cyan-800/80 dark:text-cyan-300/80 font-semibold',
      divider: 'bg-black/5 dark:bg-white/10',
      borderLeft: 'border-black/5 dark:border-white/10',
    },
    amber: {
      container: 'bg-white/80 dark:bg-[#2B2114]/80 backdrop-blur-md text-amber-950 dark:text-amber-50 focus-within:ring-2 focus-within:ring-amber-500/30 shadow-2xs',
      btnTop: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-amber-950 dark:text-amber-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      btnBottom: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-amber-950 dark:text-amber-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      unitText: 'text-amber-800/80 dark:text-amber-300/80 font-semibold',
      divider: 'bg-black/5 dark:bg-white/10',
      borderLeft: 'border-black/5 dark:border-white/10',
    },
    violet: {
      container: 'bg-white/80 dark:bg-[#241A33]/80 backdrop-blur-md text-purple-950 dark:text-purple-50 focus-within:ring-2 focus-within:ring-purple-500/30 shadow-2xs',
      btnTop: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-purple-950 dark:text-purple-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      btnBottom: 'bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-purple-950 dark:text-purple-100 active:scale-95 active:bg-black/15 dark:active:bg-white/15',
      unitText: 'text-purple-800/80 dark:text-purple-300/80 font-semibold',
      divider: 'bg-black/5 dark:bg-white/10',
      borderLeft: 'border-black/5 dark:border-white/10',
    },
  }[colorScheme];

  const plusHoldProps = useAcceleratingHold({
    onAction: () => {
      triggerHaptic(6);
      onIncrement();
    },
    disabled,
  });

  const minusHoldProps = useAcceleratingHold({
    onAction: () => {
      triggerHaptic(6);
      onDecrement();
    },
    disabled,
  });

  return (
    <div
      className={`flex-1 min-w-0 h-22 sm:h-24 md:h-[100px] rounded-3xl border flex items-stretch overflow-hidden transition-all ${colorStyles.container} ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {/* 1. NUMBER INPUT WITH DIRECT UNIT UNDERNEATH (LEFT) */}
      <div className="flex-1 flex flex-col items-center justify-center py-2 px-2 min-w-0">
        <input
          id={`input-${idPrefix}`}
          aria-label={unit}
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          value={displayValue !== undefined ? displayValue : value}
          onFocus={(e) => {
            e.target.select();
            triggerHaptic(6);
          }}
          onChange={(e) => {
            const num = parseFloat(e.target.value);
            onChange(isNaN(num) ? min : num);
          }}
          className={`w-full text-center font-mono font-extrabold bg-transparent outline-none select-all leading-none ${
            (displayValue !== undefined ? displayValue : value).toString().length >= 4
              ? 'text-2xl sm:text-3xl lg:text-4xl'
              : 'text-3xl sm:text-4xl lg:text-5xl'
          }`}
        />
        <span
          className={`text-xs sm:text-xs font-semibold select-none mt-1 tracking-wide text-center leading-none ${colorStyles.unitText}`}
        >
          {unit}
        </span>
      </div>

      {/* 2. STACKED [+] & [-] BUTTONS (RIGHT) - EACH BUTTON IS EQUALLY HIGH AS WIDE */}
      <div className={`w-11 sm:w-12 md:w-13 flex flex-col border-l shrink-0 ${colorStyles.borderLeft}`}>
        {/* [+] PLUS BUTTON ON TOP */}
        <button
          id={`btn-${idPrefix}-plus`}
          type="button"
          disabled={disabled}
          aria-label={`Verhoog ${unit}`}
          {...plusHoldProps}
          className={`flex-1 flex items-center justify-center transition-all select-none touch-manipulation cursor-pointer ${colorStyles.btnTop} ${
            disabled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
        >
          <Plus className="w-5 h-5 sm:w-6 sm:h-6 stroke-[2.8] pointer-events-none" />
        </button>

        {/* SUBTLE INNER DIVIDER */}
        <div className={`h-[1px] w-full ${colorStyles.divider}`} />

        {/* [-] MINUS BUTTON ON BOTTOM */}
        <button
          id={`btn-${idPrefix}-minus`}
          type="button"
          disabled={disabled}
          aria-label={`Verlaag ${unit}`}
          {...minusHoldProps}
          className={`flex-1 flex items-center justify-center transition-all select-none touch-manipulation cursor-pointer ${colorStyles.btnBottom} ${
            disabled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
        >
          <Minus className="w-5 h-5 sm:w-6 sm:h-6 stroke-[2.8] pointer-events-none" />
        </button>
      </div>
    </div>
  );
}
